import client from '@/utils/httpClient';
import {
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

export const conferenceKeys = {
  base: ['conferences'] as const,
  byId: (id: string) => [...conferenceKeys.base, 'byId', id],
  byOrganization: (organizationId: string) => [
    ...conferenceKeys.base,
    'byOrganization',
    organizationId,
  ],
  list: () => [...conferenceKeys.base, 'list'],
  filteredList: (filter?: string, sortOrder?: string) => [
    ...conferenceKeys.list(),
    filter,
    sortOrder,
  ],
};

type ListConference = {
  id: string;
  name: string;
  parentOrganizationName: string;
  organizationName: string;
  createdDate: Date;
  status: string;
};

export interface Conference {
  id: string;
  name: string;
  parentOrg: string;
  hostOrg: string;
  addedOn: Date;
  isActive: boolean;
  status: string;
}

/** Raw `GET /conferences/byOrganization/{orgId}` row (backend ConferenceDto) — not the mapped `Conference`. */
export interface ConferenceByOrg {
  id: string;
  name: string;
  status: string;
  organizationId: string;
  organizationName: string;
}

export interface SingleConference {
  id: string;
  name: string;
  status: string;
  organizationId: string;
  parentOrganizationId: string;
  announcement: string;
}

function getAllConferences({ pageParam = 1, filter = '', sortOrder = '-addedOn' }) {
  return client
    .get<ListConference[]>(
      `/conferences?${
        filter ? `Filters=(name|organizationName)@=*${filter}&` : ''
      }SortOrder=${sortOrder}&pageNumber=${pageParam}&PageSize=50`,
    )
    .then((response) => {
      const data = (response.data as ListConference[]).map((conference) => ({
        ...conference,
        parentOrg: conference.parentOrganizationName,
        hostOrg: conference.organizationName,
        addedOn: conference.createdDate,
        isActive: conference.status === 'Active',
      }));

      return {
        data,
        pagination: JSON.parse(response.headers['x-pagination']),
      };
    });
}

export function useGetAllConferences(filter?: string, sortOrder?: string) {
  return useInfiniteQuery(
    conferenceKeys.filteredList(filter, sortOrder),
    ({ pageParam }) => getAllConferences({ pageParam, filter, sortOrder }),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.pagination.hasNext) {
          return lastPage.pagination.pageNumber + 1;
        }
        return undefined;
      },
    },
  );
}
function getConferencesByOrg(orgId: string) {
  return client
    .get<ConferenceByOrg[]>(`/conferences/byOrganization/${orgId}`)
    .then((response) => response.data);
}

export function useGetConferencesByOrg(
  orgId: string,
  options?: UseQueryOptions<ConferenceByOrg[]>,
) {
  return useQuery(conferenceKeys.byOrganization(orgId), () => getConferencesByOrg(orgId), options);
}

function getSingleConference(conferenceId: string) {
  return client.get<SingleConference>(`/conferences/${conferenceId}`).then((response) => {
    const data = response.data as SingleConference;

    return data;
  });
}

export function useGetSingleConference(concferenceId: string) {
  return useQuery(conferenceKeys.byId(concferenceId), () => getSingleConference(concferenceId));
}

export interface CreateConference {
  name: string;
  organizationId: string;
  announcement: string;
}

async function createConference(data: CreateConference): Promise<Conference> {
  return client.post<Conference>('/conferences', data).then((response) => response.data);
}

export function useCreateConference() {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const client = useQueryClient();

  return useMutation((data: CreateConference) => createConference(data), {
    onSuccess: () => {
      client.invalidateQueries(conferenceKeys.list());
    },
  });
}

async function updateConference(
  conferenceId: string,
  data: Omit<CreateConference, 'organizationId'>,
): Promise<Conference> {
  return client
    .put<Conference>(`/conferences/${conferenceId}`, data)
    .then((response) => response.data);
}

export function useUpdateConference(conferenceId: string) {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const client = useQueryClient();

  return useMutation(
    (data: Omit<CreateConference, 'organizationId'>) => updateConference(conferenceId, data),
    {
      onSuccess: () => {
        client.invalidateQueries(conferenceKeys.list());
      },
    },
  );
}

export const activateConference = (conferenceId: string) =>
  client.put(`/conferences/${conferenceId}/activate`);

export const deactivateConference = (conferenceId: string) =>
  client.put(`/conferences/${conferenceId}/deactivate`);
